<template>
  <div class="main-layout">
    <!-- Sidebar -->
    <AppSidebar v-model="sidebarOpen" />

    <!-- Contenedor principal -->
    <div class="main-container" :class="{ 'sidebar-collapsed': !sidebarOpen }">
      <!-- Top Bar -->
      <AppHeader :sidebar-open="sidebarOpen" @toggle-sidebar="sidebarOpen = !sidebarOpen" />

      <!-- Contenido -->
      <div class="main-content">
        <div class="content-wrapper">
          <slot />
        </div>
      </div>
    </div>

    <!-- Modal obligatorio de cambio de contraseña -->
    <v-dialog
      v-model="mostrarModalCambioPassword"
      persistent
      max-width="450"
      :close-on-back="false"
    >
      <v-card>
        <v-card-title class="d-flex align-center pa-4 bg-warning">
          <v-icon color="white" class="mr-2">mdi-lock-alert</v-icon>
          <span class="text-white">Cambio de Contraseña Requerido</span>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Por seguridad, debe cambiar su contraseña antes de continuar usando el sistema.
          </v-alert>

          <v-form ref="formCambioPassword" @submit.prevent="cambiarPassword">
            <!-- Mostrar campo de contraseña actual solo si no hay una guardada del login -->
            <v-text-field
              v-if="!tienePasswordGuardada"
              v-model="passwordActual"
              :type="mostrarPasswordActual ? 'text' : 'password'"
              label="Contraseña Actual"
              prepend-inner-icon="mdi-lock"
              :append-inner-icon="mostrarPasswordActual ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="mostrarPasswordActual = !mostrarPasswordActual"
              :rules="[v => !!v || 'La contraseña actual es requerida']"
              density="comfortable"
              class="mb-3"
              autocomplete="current-password"
            />

            <v-text-field
              v-model="passwordNueva"
              :type="mostrarPasswordNueva ? 'text' : 'password'"
              label="Nueva Contraseña"
              prepend-inner-icon="mdi-lock-plus"
              :append-inner-icon="mostrarPasswordNueva ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="mostrarPasswordNueva = !mostrarPasswordNueva"
              :rules="reglasPasswordNueva"
              density="comfortable"
              class="mb-3"
              autocomplete="new-password"
              hint="Mínimo 6 caracteres"
              persistent-hint
            />

            <v-text-field
              v-model="passwordConfirmar"
              :type="mostrarPasswordConfirmar ? 'text' : 'password'"
              label="Confirmar Nueva Contraseña"
              prepend-inner-icon="mdi-lock-check"
              :append-inner-icon="mostrarPasswordConfirmar ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="mostrarPasswordConfirmar = !mostrarPasswordConfirmar"
              :rules="reglasConfirmarPassword"
              density="comfortable"
              autocomplete="new-password"
            />
          </v-form>

          <v-alert
            v-if="errorCambioPassword"
            type="error"
            variant="tonal"
            density="compact"
            class="mt-4"
          >
            {{ errorCambioPassword }}
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn
            color="warning"
            variant="elevated"
            :loading="cambiandoPassword"
            :disabled="cambiandoPassword"
            @click="cambiarPassword"
          >
            <v-icon start>mdi-lock-reset</v-icon>
            Cambiar Contraseña
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar de éxito -->
    <v-snackbar v-model="snackbarExito" color="success" timeout="3000">
      <v-icon start>mdi-check-circle</v-icon>
      Contraseña cambiada exitosamente
    </v-snackbar>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import AppSidebar from './AppSidebar.vue'
import AppHeader from './AppHeader.vue'

export default {
  name: 'MainLayout',
  components: {
    AppSidebar,
    AppHeader
  },
  setup() {
    const store = useStore()
    const sidebarOpen = ref(true)

    // Modal de cambio de contraseña
    const formCambioPassword = ref(null)
    const passwordActual = ref('')
    const passwordNueva = ref('')
    const passwordConfirmar = ref('')
    const mostrarPasswordActual = ref(false)
    const mostrarPasswordNueva = ref(false)
    const mostrarPasswordConfirmar = ref(false)
    const cambiandoPassword = ref(false)
    const errorCambioPassword = ref('')
    const snackbarExito = ref(false)

    // Computed para verificar si se debe mostrar el modal
    const mostrarModalCambioPassword = computed(() => {
      return store.getters['auth/requiereCambioPwd']
    })

    // Computed para verificar si hay una contraseña guardada del login
    const tienePasswordGuardada = computed(() => {
      return !!store.getters['auth/tempPasswordForChange']
    })

    // Reglas de validación
    const reglasPasswordNueva = [
      v => !!v || 'La nueva contraseña es requerida',
      v => (v && v.length >= 6) || 'La contraseña debe tener al menos 6 caracteres'
    ]

    const reglasConfirmarPassword = [
      v => !!v || 'Debe confirmar la contraseña',
      v => v === passwordNueva.value || 'Las contraseñas no coinciden'
    ]

    // Función para cambiar contraseña
    const cambiarPassword = async () => {
      // Validar formulario
      const { valid } = await formCambioPassword.value.validate()
      if (!valid) return

      cambiandoPassword.value = true
      errorCambioPassword.value = ''

      try {
        // Usar contraseña guardada del login o la ingresada manualmente
        const currentPwd = store.getters['auth/tempPasswordForChange'] || passwordActual.value

        if (!currentPwd) {
          errorCambioPassword.value = 'Debe ingresar la contraseña actual.'
          return
        }

        const result = await store.dispatch('auth/changePassword', {
          currentPassword: currentPwd,
          newPassword: passwordNueva.value
        })

        if (result.success) {
          // Limpiar formulario
          passwordActual.value = ''
          passwordNueva.value = ''
          passwordConfirmar.value = ''
          snackbarExito.value = true
        } else {
          errorCambioPassword.value = result.message
        }
      } catch (error) {
        errorCambioPassword.value = 'Error al cambiar la contraseña. Intente nuevamente.'
      } finally {
        cambiandoPassword.value = false
      }
    }

    return {
      sidebarOpen,
      // Modal cambio password
      formCambioPassword,
      mostrarModalCambioPassword,
      tienePasswordGuardada,
      passwordActual,
      passwordNueva,
      passwordConfirmar,
      mostrarPasswordActual,
      mostrarPasswordNueva,
      mostrarPasswordConfirmar,
      cambiandoPassword,
      errorCambioPassword,
      snackbarExito,
      reglasPasswordNueva,
      reglasConfirmarPassword,
      cambiarPassword
    }
  }
}
</script>

<style scoped>
.main-layout {
  display: flex;
  min-height: 100vh;
  background-color: #F8FAFC;
}

.main-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 240px;
  transition: margin-left 0.3s ease, width 0.3s ease;
  height: 100vh;
  overflow: hidden;
  width: calc(100% - 240px);
  min-width: 0;
}

.main-container.sidebar-collapsed {
  margin-left: 0;
  width: 100%;
}

.main-content {
  flex: 1;
  background-color: #F8FAFC;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  height: 0; /* Forzar que el contenedor respete el flex y permita scroll */
}

.content-wrapper {
  padding: 20px 24px 16px 24px;
  min-height: 100%;
}

@media (max-width: 960px) {
  .main-container {
    margin-left: 0;
  }

  .content-wrapper {
    padding: 12px 16px;
  }
}
</style>

<!-- Estilos globales para Dark Mode - Paleta Slate Moderna -->
<style>
/* Dark mode: Fondo oscuro para el layout principal */
.v-theme--dark .main-layout,
.v-theme--darkTheme .main-layout {
  background-color: #0F172A;
}

.v-theme--dark .main-content,
.v-theme--darkTheme .main-content {
  background-color: #0F172A;
}

/* Dark mode: Asegurar que el texto en el contenido sea legible */
.v-theme--dark .content-wrapper,
.v-theme--darkTheme .content-wrapper {
  color: #E0E0E0;
}

/* Dark mode: Títulos y subtítulos */
.v-theme--dark .content-wrapper h1,
.v-theme--dark .content-wrapper h2,
.v-theme--dark .content-wrapper h3,
.v-theme--darkTheme .content-wrapper h1,
.v-theme--darkTheme .content-wrapper h2,
.v-theme--darkTheme .content-wrapper h3 {
  color: #FFFFFF;
}

/* Dark mode: Texto secundario */
.v-theme--dark .content-wrapper .text-grey,
.v-theme--darkTheme .content-wrapper .text-grey {
  color: #BDBDBD !important;
}
</style>
